// ✅ src/pages/OrderManagement/ProjectEntryForm.jsx
// Update-only Project Entry (ASPX parity) – FULL FILE
//
// Uses APIs:
//   GET  https://nissi-india.com/api/projectEntry/view?contractId=1&branchtype=HO&branchid=8
//   POST https://nissi-india.com/api/projectEntry/save
//
// Notes:
// - contractId comes from URL: /OrderManagement/ProjectEntry?contractId=1
// - Save strategy: delete + insert in backend (same as ASPX.CS)
// - UI uses <input type="date"> and converts to/from dd/MM/yyyy for backend safely.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FaBars,
  FaHome,
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaChartBar,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBuilding,
  FaTasks,
  FaWarehouse,
  FaUsers,
  FaSave,
  FaUndo,
  FaPrint,
  FaEye,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/projectEntry";

const padTo = (arr, n, factory = () => ({})) => {
  const out = Array.isArray(arr) ? [...arr] : [];
  while (out.length < n) out.push(factory());
  return out;
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

/** dd/MM/yyyy -> yyyy-MM-dd for <input type="date"> */
const dmyToISO = (dmy) => {
  const s = toStr(dmy).trim();
  if (!s) return "";
  const [dd, mm, yyyy] = s.split("/");
  if (!dd || !mm || !yyyy) return "";
  const iso = `${yyyy.padStart(4, "0")}-${mm.padStart(2, "0")}-${dd.padStart(
    2,
    "0"
  )}`;
  return iso;
};

/** yyyy-MM-dd -> dd/MM/yyyy for backend */
const isoToDMY = (iso) => {
  const s = toStr(iso).trim();
  if (!s) return "";
  const [yyyy, mm, dd] = s.split("-");
  if (!dd || !mm || !yyyy) return "";
  return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy.padStart(
    4,
    "0"
  )}`;
};

const moneyToNumber = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/Rs\./gi, "").replace(/,/g, "").trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoneyIN = (v) =>
  moneyToNumber(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const emptyEmdRow = () => ({
  dataValue: "",
  amount: "",
  inForm: "",
  instrumentNo: "",
  from: "",
  to: "",
  mode: "",
  remarks: "",
  receivedOn: "",
});

const emptyPerfRow = () => ({
  dataValue: "",
  amount: "",
  inForm: "",
  instrumentNo: "",
  from: "",
  to: "",
  mode: "",
  remarks: "",
  receivedOn: "",
});

const emptyRetentionRow = () => ({
  dataValue: "",
  amount: "",
  inForm: "",
  instrumentNo: "",
  from: "",
  to: "",
  mode: "",
  remarks: "",
  receivedOn: "",
});

const emptyMobRow = () => ({
  dataValue: "",
  amount: "",
  interest: "",
  inForm: "",
  instrumentNo: "",
  from: "",
  to: "",
  mode: "",
  remarks: "",
  receivedOn: "",
});

const emptyTaxRow = (taxType = "") => ({
  taxType,
  category: "",
  include: "",
  reimbursable: 0,
  percentage: "",
  remarks: "",
});

const emptyOtherDetails = () => ({
  carPolicyNo: "",
  carSumInsured: "",
  carPremiumAmt: "",
  carValidFrom: "",
  carValidTo: "",
  carRemarks: "",

  mainPerFrom: "",
  mainPerTo: "",
  mainPerRemarks: "",

  wcPolicyNo: "",
  wcSumInsured: "",
  wcPremiumAmt: "",
  wcValidFrom: "",
  wcValidTo: "",
  wcRemarks: "",

  labourLicenceNo: "",
  securityDeposit: "",
  labourInform: "",
  labourInstrNo: "",
  labourMode: "",
  labourFrom: "",
  labourTo: "",
  labourRemarks: "",

  esiNo: "",
  esiZone: "",
  esiCircle: "",
  esiMinNo: "",
  esiRemarks: "",
});

const normalizeDateToISO = (v) => {
  const s = toStr(v).trim();
  if (!s) return "";
  if (s.includes("/")) return dmyToISO(s);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const Input = ({ isDark, ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      isDark
        ? "bg-gray-700 border-gray-600 text-white"
        : "bg-white border-gray-300 text-gray-900"
    } ${props.className || ""}`}
  />
);

const TextArea = ({ isDark, ...props }) => (
  <textarea
    {...props}
    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      isDark
        ? "bg-gray-700 border-gray-600 text-white"
        : "bg-white border-gray-300 text-gray-900"
    } ${props.className || ""}`}
  />
);

const Select = ({ isDark, children, ...props }) => (
  <select
    {...props}
    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      isDark
        ? "bg-gray-700 border-gray-600 text-white"
        : "bg-white border-gray-300 text-gray-900"
    } ${props.className || ""}`}
  >
    {children}
  </select>
);

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">
    {children}
  </th>
);

const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

const Section = ({ isDark, title, color = "bg-blue-600", children }) => (
  <div
    className={`rounded-2xl shadow-xl border ${
      isDark
        ? "bg-gray-800/50 backdrop-blur-md border-gray-700"
        : "bg-white border-gray-200"
    }`}
  >
    <div className={`${color} text-white px-6 py-3 rounded-t-2xl`}>
      <h2 className="text-lg font-bold">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Label = ({ isDark, children, required }) => (
  <label
    className={`block text-sm font-medium mb-2 ${
      isDark ? "text-gray-300" : "text-gray-700"
    }`}
  >
    {children} {required ? <span className="text-red-500">*</span> : null}
  </label>
);

const TableShell = ({ isDark, title, rows, setRows, rowFactory, columns }) => {
  const onCell = (idx, key, val) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || rowFactory()), [key]: val };
      return next;
    });
  };

  return (
    <Section isDark={isDark} title={title} color="bg-blue-600">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-blue-600 text-white">
            <tr>
              {columns.map((c) => (
                <Th key={c.key}>{c.title}</Th>
              ))}
            </tr>
          </thead>

          <tbody
            className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
              isDark ? "divide-gray-700" : "divide-gray-200"
            }`}
          >
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <Td key={c.key}>{c.render({ row: r, index: i, onCell })}</Td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

export default function ProjectEntryForm() {
  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
const contractId = useMemo(
  () => parseInt((qs.get("contractId") || qs.get("contractid") || "0").trim(), 10),
  [qs]
);

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  const loginUserId = useMemo(() => {
    const n = Number(userIdLS || 0);
    return Number.isFinite(n) ? n : 0;
  }, [userIdLS]);

  const branchtype = useMemo(() => branchTypeLS || "HO", [branchTypeLS]);

  const branchid = useMemo(() => {
    const n = Number(branchIdLS || 0);
    return Number.isFinite(n) ? n : 0;
  }, [branchIdLS]);

  const sidebarItems = useMemo(
    () => [
      {
        name: "Date/LOA",
        icon: FaCalendarAlt,
        path: `/OrderManagement/ProjectEntry?contractId=${contractId}`,
      },
      {
        name: "BOQ/Operative",
        icon: FaFileInvoiceDollar,
        path: `/OrderManagement/BOQ?contractid=${contractId}`,
      },
      {
        name: "Program/DPR",
        icon: FaChartBar,
        path: `/OrderManagement/DailyProgressMonthWise?contractid=${contractId}`,
      },
      {
        name: "Purchase Order",
        icon: FaFileAlt,
        path: "/OrderManagement/PurchaseOrderListing",
      },
      {
        name: "Rate Approval",
        icon: FaCheckCircle,
        path: "/OrderManagement/RateApprove",
      },
      {
        name: "Risk/Issues",
        icon: FaExclamationTriangle,
        path: "/OrderManagement/RiskAndIssue",
      },
      {
        name: "Documentation",
        icon: FaFileAlt,
        path: "/OrderManagement/ProjectEntryDocumentation",
      },
      {
        name: "Photos",
        icon: FaBuilding,
        path: "/OrderManagement/PhotoDocument",
      },
      {
        name: "Reconciliation",
        icon: FaTasks,
        path: "/OrderManagement/Reconciliation",
      },
      {
        name: "Do/Don't's",
        icon: FaWarehouse,
        path: "/OrderManagement/Rules",
      },
      {
        name: "Vendor Details",
        icon: FaUsers,
        path: "/OrderManagement/ProjectVendorDetails",
      },
    ],
    [contractId]
  );

  const [branches, setBranches] = useState([]);
  const [workLocations, setWorkLocations] = useState([]);

  const [master, setMaster] = useState({
    branchId: "",
    contractName: "",
    clientRefValue: "",
    clientName: "",
    workLocationId: "",
    natureOfWork: "",
    srcIdNo: "",
    contractValueText: "",
    contractType: "",

    clientContact: "",
    pmcRefValue: "",
    pmcName: "",
    pmcContact: "",
    consultantRefValue: "",
    consultantName: "",
    consultantContact: "",

    loaNo: "",
    agreementNo: "",
    reasonOfDelay: "",
    ldClause: "",
    defectLiability: "",
    supRef1: "",
    supValue1: "",
    supRef2: "",
    supValue2: "",

    dateOfLoa: "",
    dateOfAgreement: "",
    startDate: "",
    completionDate: "",
    antipDate: "",

    defectPeriodFrom: "",
    defectPeriodTo: "",

    extensionTimeStatus: 0,
    extensionTime: "",

    materialSupplyStatus: 0,
    contractDuration: "",
  });

  const [emdSecurity, setEmdSecurity] = useState(padTo([], 2, emptyEmdRow));
  const [performanceSecurity, setPerformanceSecurity] = useState(
    padTo([], 2, emptyPerfRow)
  );
  const [mobilisation, setMobilisation] = useState(padTo([], 4, emptyMobRow));
  const [retention, setRetention] = useState(
    padTo([], 2, emptyRetentionRow)
  );
  const [taxDetails, setTaxDetails] = useState([
    emptyTaxRow("Work contract tax"),
    emptyTaxRow("Service tax"),
    emptyTaxRow("Labour welfare tax"),
  ]);
  const [otherDetails, setOtherDetails] = useState(emptyOtherDetails());

  const initialRef = useRef(null);

  const restoreSnapshot = (snap) => {
    if (!snap) return;
    setBranches(snap.branches || []);
    setWorkLocations(snap.workLocations || []);
    setMaster(snap.master || master);
    setEmdSecurity(snap.emdSecurity || padTo([], 2, emptyEmdRow));
    setPerformanceSecurity(
      snap.performanceSecurity || padTo([], 2, emptyPerfRow)
    );
    setMobilisation(snap.mobilisation || padTo([], 4, emptyMobRow));
    setRetention(snap.retention || padTo([], 2, emptyRetentionRow));
    setTaxDetails(
      (snap.taxDetails && snap.taxDetails.length
        ? snap.taxDetails
        : [
            emptyTaxRow("Work contract tax"),
            emptyTaxRow("Service tax"),
            emptyTaxRow("Labour welfare tax"),
          ]
      ).map((r) => ({
        ...emptyTaxRow(r.taxType || r.fld_taxtype || ""),
        ...r,
        reimbursable: Number(r.reimbursable ?? r.fld_reimbursable ?? 0) ? 1 : 0,
      }))
    );
    setOtherDetails({ ...emptyOtherDetails(), ...(snap.otherDetails || {}) });
  };

  const normalizeViewToState = (data) => {
    const dd = data || {};
    const dds = dd.dropdowns || {};
    const m = dd.master || {};
    const t = dd.tables || {};

    const branchList = (dds.branches || []).map((x) => ({
      id: x.id ?? x.fld_Branchid ?? x.branchid,
      name: x.name ?? x.fld_BranchName ?? x.branchname,
    }));
    const wlList = (dds.workLocations || []).map((x) => ({
      id: x.id ?? x.fld_Worklocationid ?? x.worklocationid,
      name: x.name ?? x.fld_Worklocationname ?? x.worklocationname,
    }));

    const newMaster = {
      branchId: toStr(m.fld_linkbranchid || m.fld_branchid || m.branchId || ""),
      contractName: toStr(m.fld_contractname || m.contractName || ""),
      clientRefValue: toStr(m.fld_clientrefvalue || m.clientRefValue || ""),
      clientName: toStr(m.fld_clientname || m.clientName || ""),
      workLocationId: toStr(m.fld_locationid || m.workLocationId || ""),
      natureOfWork: toStr(m.fld_natureofwork || m.natureOfWork || ""),
      srcIdNo: toStr(m.fld_srcidno || m.srcIdNo || ""),
      contractValueText: toStr(m.fld_contractvalue1 || m.contractValueText || ""),
      contractType: toStr(m.fld_contracttype || m.contractType || ""),

      clientContact: toStr(m.fld_clientContact || m.clientContact || ""),
      pmcRefValue: toStr(m.fld_PMCrefvalue || m.pmcRefValue || ""),
      pmcName: toStr(m.fld_PMCName || m.fld_PMCname || m.pmcName || ""),
      pmcContact: toStr(m.fld_PMCContact || m.fld_PMCcontact || m.pmcContact || ""),
      consultantRefValue: toStr(
        m.fld_consultantrefvalue || m.consultantRefValue || ""
      ),
      consultantName: toStr(m.fld_consultantName || m.consultantName || ""),
      consultantContact: toStr(
        m.fld_consultantContact || m.consultantContact || ""
      ),

      loaNo: toStr(m.fld_LOAno || m.loaNo || ""),
      agreementNo: toStr(m.fld_agreementNo || m.agreementNo || ""),
      reasonOfDelay: toStr(m.fld_reasonofdelay || m.reasonOfDelay || ""),
      ldClause: toStr(m.fld_ldclause || m.ldClause || ""),
      defectLiability: toStr(m.fld_defectliability || m.defectLiability || ""),
      supRef1: toStr(m.fld_supref1 || m.supRef1 || ""),
      supValue1: toStr(m.fld_supvalue1 || m.supValue1 || ""),
      supRef2: toStr(m.fld_supref2 || m.supRef2 || ""),
      supValue2: toStr(m.fld_supvalue2 || m.supValue2 || ""),

      dateOfLoa: normalizeDateToISO(m.fld_dateofloa || m.dateOfLoa || ""),
      dateOfAgreement: normalizeDateToISO(
        m.fld_dateofagreement || m.dateOfAgreement || ""
      ),
      startDate: normalizeDateToISO(m.fld_startdate || m.startDate || ""),
      completionDate: normalizeDateToISO(
        m.fld_completiondate || m.completionDate || ""
      ),
      antipDate: normalizeDateToISO(m.fld_antipdate || m.antipDate || ""),

      defectPeriodFrom: normalizeDateToISO(
        m.fld_defectliabilityfrom || m.defectPeriodFrom || ""
      ),
      defectPeriodTo: normalizeDateToISO(
        m.fld_defectliabilityto || m.defectPeriodTo || ""
      ),

      extensionTimeStatus: Number(
        m.fld_extensiontimestatus ?? m.extensionTimeStatus ?? 0
      )
        ? 1
        : 0,
      extensionTime: normalizeDateToISO(m.fld_extensiontime || m.extensionTime || ""),

      materialSupplyStatus: Number(
        m.fld_materialsupplystatus ?? m.materialSupplyStatus ?? 0
      )
        ? 1
        : 0,
      contractDuration: toStr(m.fld_contractduration || m.contractDuration || ""),
    };

    const mapGridRow = (r, type) => {
      if (!r) return type === "mob" ? emptyMobRow() : emptyEmdRow();
      if (type === "emd") {
        return {
          ...emptyEmdRow(),
          dataValue: toStr(r.fld_emddatavalue ?? r.dataValue ?? ""),
          amount: toStr(r.fld_emdbidsecurityamt ?? r.amount ?? ""),
          inForm: toStr(r.fld_emdbidsecamtintheform ?? r.inForm ?? ""),
          instrumentNo: toStr(r.fld_emdInstNo ?? r.instrumentNo ?? ""),
          from: normalizeDateToISO(r.fld_emdvalidityfrom ?? r.from ?? ""),
          to: normalizeDateToISO(r.fld_emdvalidityto ?? r.to ?? ""),
          mode: toStr(r.fld_emdmode ?? r.mode ?? ""),
          remarks: toStr(r.fld_emdbidsecurityamtremarks ?? r.remarks ?? ""),
          receivedOn: normalizeDateToISO(r.fld_emdReceivedon ?? r.receivedOn ?? ""),
        };
      }
      if (type === "perf") {
        return {
          ...emptyPerfRow(),
          dataValue: toStr(r.fld_Perfdatavalue ?? r.dataValue ?? ""),
          amount: toStr(r.fld_PerfSecAmt ?? r.amount ?? ""),
          inForm: toStr(r.fld_PerfSecAmtintheform ?? r.inForm ?? ""),
          instrumentNo: toStr(r.fld_PerfSecInstNo ?? r.instrumentNo ?? ""),
          from: normalizeDateToISO(r.fld_PerfSecValfrom ?? r.from ?? ""),
          to: normalizeDateToISO(r.fld_PerfSecValto ?? r.to ?? ""),
          mode: toStr(r.fld_PerfSecMode ?? r.mode ?? ""),
          remarks: toStr(r.fld_PerfSecurityRemarks ?? r.remarks ?? ""),
          receivedOn: normalizeDateToISO(r.fld_perfReceivedon ?? r.receivedOn ?? ""),
        };
      }
      if (type === "ret") {
        return {
          ...emptyRetentionRow(),
          dataValue: toStr(r.fld_Retentiondataval ?? r.dataValue ?? ""),
          amount: toStr(r.fld_retentionamount ?? r.amount ?? ""),
          inForm: toStr(r.fld_Retentiontintheform ?? r.inForm ?? ""),
          instrumentNo: toStr(r.fld_RetentionInstNo ?? r.instrumentNo ?? ""),
          from: normalizeDateToISO(r.fld_RetValfrom ?? r.from ?? ""),
          to: normalizeDateToISO(r.fld_RetValto ?? r.to ?? ""),
          mode: toStr(r.fld_RetentionMode ?? r.mode ?? ""),
          remarks: toStr(r.fld_RetentionRemarks ?? r.remarks ?? ""),
          receivedOn: normalizeDateToISO(r.fld_RetReceivedon ?? r.receivedOn ?? ""),
        };
      }
      return {
        ...emptyMobRow(),
        dataValue: toStr(r.fld_Mobdataval ?? r.dataValue ?? ""),
        amount: toStr(r.fld_MobAmount ?? r.amount ?? ""),
        interest: toStr(r.fld_interest ?? r.interest ?? ""),
        inForm: toStr(r.fld_Mobintheform ?? r.inForm ?? ""),
        instrumentNo: toStr(r.fld_MobInstNo ?? r.instrumentNo ?? ""),
        from: normalizeDateToISO(r.fld_fromdate ?? r.from ?? ""),
        to: normalizeDateToISO(r.fld_todate ?? r.to ?? ""),
        mode: toStr(r.fld_MobMode ?? r.mode ?? ""),
        remarks: toStr(r.fld_MobRemarks ?? r.remarks ?? ""),
        receivedOn: normalizeDateToISO(r.fld_MobReceivedon ?? r.receivedOn ?? ""),
      };
    };

    const emd = padTo(
      (t.emdSecurity || []).map((r) => mapGridRow(r, "emd")),
      2,
      emptyEmdRow
    );
    const perf = padTo(
      (t.performanceSecurity || []).map((r) => mapGridRow(r, "perf")),
      2,
      emptyPerfRow
    );
    const mob = padTo(
      (t.mobilisation || []).map((r) => mapGridRow(r, "mob")),
      4,
      emptyMobRow
    );
    const ret = padTo(
      (t.retention || []).map((r) => mapGridRow(r, "ret")),
      2,
      emptyRetentionRow
    );

    const tax =
      (t.taxDetails || []).length > 0
        ? (t.taxDetails || []).map((r) => ({
            ...emptyTaxRow(toStr(r.fld_taxtype ?? r.taxType ?? "")),
            taxType: toStr(r.fld_taxtype ?? r.taxType ?? ""),
            category: toStr(r.fld_taxCategory ?? r.category ?? ""),
            include: toStr(r.fld_TaxInclude ?? r.include ?? ""),
            reimbursable: Number(r.fld_reimbursable ?? r.reimbursable ?? 0)
              ? 1
              : 0,
            percentage: toStr(r.fld_taxPercentage ?? r.percentage ?? ""),
            remarks: toStr(r.fld_Remarks ?? r.remarks ?? ""),
          }))
        : [
            emptyTaxRow("Work contract tax"),
            emptyTaxRow("Service tax"),
            emptyTaxRow("Labour welfare tax"),
          ];

    const od = t.otherDetails || {};
    const newOther = {
      ...emptyOtherDetails(),

      carPolicyNo: toStr(od.fld_carPolicyNo ?? od.carPolicyNo ?? ""),
      carSumInsured: toStr(od.fld_carSumInsured ?? od.carSumInsured ?? ""),
      carPremiumAmt: toStr(od.fld_carPremiumAmt ?? od.carPremiumAmt ?? ""),
      carValidFrom: normalizeDateToISO(od.fld_carValidFrom ?? od.carValidFrom ?? ""),
      carValidTo: normalizeDateToISO(od.fld_carValidTo ?? od.carValidTo ?? ""),
      carRemarks: toStr(od.fld_carRemarks ?? od.carRemarks ?? ""),

      mainPerFrom: normalizeDateToISO(od.fld_mainperfrom ?? od.mainPerFrom ?? ""),
      mainPerTo: normalizeDateToISO(od.fld_mainperto ?? od.mainPerTo ?? ""),
      mainPerRemarks: toStr(od.fld_mainperRemarks ?? od.mainPerRemarks ?? ""),

      wcPolicyNo: toStr(od.fld_wcPolicyNo ?? od.wcPolicyNo ?? ""),
      wcSumInsured: toStr(od.fld_wcSumInsured ?? od.wcSumInsured ?? ""),
      wcPremiumAmt: toStr(od.fld_wcPremiumAmt ?? od.wcPremiumAmt ?? ""),
      wcValidFrom: normalizeDateToISO(od.fld_wcValidFrom ?? od.wcValidFrom ?? ""),
      wcValidTo: normalizeDateToISO(od.fld_wcValidTo ?? od.wcValidTo ?? ""),
      wcRemarks: toStr(od.fld_wcRemarks ?? od.wcRemarks ?? ""),

      labourLicenceNo: toStr(od.fld_labourLicenceNo ?? od.labourLicenceNo ?? ""),
      securityDeposit: toStr(od.fld_securityDeposit ?? od.securityDeposit ?? ""),
      labourInform: toStr(od.fld_labourInform ?? od.labourInform ?? ""),
      labourInstrNo: toStr(od.fld_labourInstrNo ?? od.labourInstrNo ?? ""),
      labourMode: toStr(od.fld_labourMode ?? od.labourMode ?? ""),
      labourFrom: normalizeDateToISO(od.fld_labourFrom ?? od.labourFrom ?? ""),
      labourTo: normalizeDateToISO(od.fld_labourTo ?? od.labourTo ?? ""),
      labourRemarks: toStr(od.fld_labour_Remarks ?? od.labourRemarks ?? ""),

      esiNo: toStr(od.fld_ESINo ?? od.esiNo ?? ""),
      esiZone: toStr(od.fld_ESIZone ?? od.esiZone ?? ""),
      esiCircle: toStr(od.fld_ESICircle ?? od.esiCircle ?? ""),
      esiMinNo: toStr(od.fld_ESIMinNo ?? od.esiMinNo ?? ""),
      esiRemarks: toStr(od.fld_ESIRemarks ?? od.esiRemarks ?? ""),
    };

    return { branchList, wlList, newMaster, emd, perf, mob, ret, tax, newOther };
  };

  const loadView = async () => {
    if (!contractId || contractId === 0) {
      toast.error("contractId missing in URL");
      return;
    }
    setLoading(true);
    try {
      const url = `${API_BASE}/view?contractId=${contractId}&branchtype=${encodeURIComponent(
        branchtype
      )}&branchid=${encodeURIComponent(branchid)}`;

      const res = await axios.get(url, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "View failed");
        return;
      }

      const norm = normalizeViewToState(res.data);
      setBranches(norm.branchList);
      setWorkLocations(norm.wlList);
      setMaster(norm.newMaster);
      setEmdSecurity(norm.emd);
      setPerformanceSecurity(norm.perf);
      setMobilisation(norm.mob);
      setRetention(norm.ret);
      setTaxDetails(norm.tax);
      setOtherDetails(norm.newOther);

      initialRef.current = {
        branches: norm.branchList,
        workLocations: norm.wlList,
        master: norm.newMaster,
        emdSecurity: norm.emd,
        performanceSecurity: norm.perf,
        mobilisation: norm.mob,
        retention: norm.ret,
        taxDetails: norm.tax,
        otherDetails: norm.newOther,
      };
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadView();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

console.log("URL:", window.location.href);
console.log("location.search:", location.search);
console.log("qs contractId:", qs.get("contractId"));
console.log("contractId:", contractId);
  const validate = () => {
    if (!toStr(master.contractName).trim()) {
      toast.error("Contract Name is required");
      return false;
    }
    if (!toStr(master.workLocationId).trim() || master.workLocationId === "Select") {
      toast.error("Work Location is required");
      return false;
    }
    if (!loginUserId) {
      toast.error("loginUserId missing in localStorage");
      return false;
    }
    return true;
  };

  const cleanGrid = (arr, type) => {
    const isEmpty = (r) => {
      const keys = Object.keys(r || {});
      return keys.length === 0 || keys.every((k) => toStr(r[k]).trim() === "");
    };
    const filtered = (arr || []).filter((r) => !isEmpty(r));

    return filtered.map((r) => {
      const base = { ...(r || {}) };
      if (type !== "tax") {
        base.from = normalizeDateToISO(base.from);
        base.to = normalizeDateToISO(base.to);
        base.receivedOn = normalizeDateToISO(base.receivedOn);
      }
      return base;
    });
  };

  const buildSavePayload = () => {
    return {
      contractId,
      loginUserId,
      master: {
        branchId: master.branchId ? Number(master.branchId) : null,
        contractName: master.contractName,
        clientRefValue: master.clientRefValue,
        clientName: master.clientName,
        workLocationId: master.workLocationId ? Number(master.workLocationId) : null,
        natureOfWork: master.natureOfWork,
        srcIdNo: master.srcIdNo,
        contractValueText: master.contractValueText,
        contractType: master.contractType,

        clientContact: master.clientContact,
        pmcRefValue: master.pmcRefValue,
        pmcName: master.pmcName,
        pmcContact: master.pmcContact,
        consultantRefValue: master.consultantRefValue,
        consultantName: master.consultantName,
        consultantContact: master.consultantContact,

        loaNo: master.loaNo,
        agreementNo: master.agreementNo,
        reasonOfDelay: master.reasonOfDelay,
        ldClause: master.ldClause,
        defectLiability: master.defectLiability,
        supRef1: master.supRef1,
        supValue1: master.supValue1,
        supRef2: master.supRef2,
        supValue2: master.supValue2,

        dateOfLoa: isoToDMY(master.dateOfLoa),
        dateOfAgreement: isoToDMY(master.dateOfAgreement),
        startDate: isoToDMY(master.startDate),
        completionDate: isoToDMY(master.completionDate),
        antipDate: isoToDMY(master.antipDate),

        defectPeriodFrom: isoToDMY(master.defectPeriodFrom),
        defectPeriodTo: isoToDMY(master.defectPeriodTo),

        extensionTimeStatus: master.extensionTimeStatus ? 1 : 0,
        extensionTime: isoToDMY(master.extensionTime),

        materialSupplyStatus: master.materialSupplyStatus ? 1 : 0,
        contractDuration: master.contractDuration,
      },
      tables: {
        emdSecurity: cleanGrid(emdSecurity, "emd").map((r) => ({
          dataValue: r.dataValue,
          amount: moneyToNumber(r.amount),
          inForm: r.inForm,
          instrumentNo: r.instrumentNo,
          from: r.from,
          to: r.to,
          mode: r.mode,
          remarks: r.remarks,
          receivedOn: r.receivedOn,
        })),
        performanceSecurity: cleanGrid(performanceSecurity, "perf").map((r) => ({
          dataValue: r.dataValue,
          amount: moneyToNumber(r.amount),
          inForm: r.inForm,
          instrumentNo: r.instrumentNo,
          from: r.from,
          to: r.to,
          mode: r.mode,
          remarks: r.remarks,
          receivedOn: r.receivedOn,
        })),
        mobilisation: cleanGrid(mobilisation, "mob").map((r) => ({
          dataValue: r.dataValue,
          amount: moneyToNumber(r.amount),
          interest: r.interest,
          inForm: r.inForm,
          instrumentNo: r.instrumentNo,
          from: r.from,
          to: r.to,
          mode: r.mode,
          remarks: r.remarks,
          receivedOn: r.receivedOn,
        })),
        retention: cleanGrid(retention, "ret").map((r) => ({
          dataValue: r.dataValue,
          amount: moneyToNumber(r.amount),
          inForm: r.inForm,
          instrumentNo: r.instrumentNo,
          from: r.from,
          to: r.to,
          mode: r.mode,
          remarks: r.remarks,
          receivedOn: r.receivedOn,
        })),
        taxDetails: (taxDetails || [])
          .filter((r) => toStr(r.taxType).trim() !== "")
          .map((r) => ({
            taxType: r.taxType,
            category: r.category,
            include: r.include,
            reimbursable: Number(r.reimbursable) ? 1 : 0,
            percentage: r.percentage,
            remarks: r.remarks,
          })),
        otherDetails: {
          carPolicyNo: otherDetails.carPolicyNo,
          carSumInsured: moneyToNumber(otherDetails.carSumInsured),
          carPremiumAmt: moneyToNumber(otherDetails.carPremiumAmt),
          carValidFrom: otherDetails.carValidFrom,
          carValidTo: otherDetails.carValidTo,
          carRemarks: otherDetails.carRemarks,

          mainPerFrom: otherDetails.mainPerFrom,
          mainPerTo: otherDetails.mainPerTo,
          mainPerRemarks: otherDetails.mainPerRemarks,

          wcPolicyNo: otherDetails.wcPolicyNo,
          wcSumInsured: moneyToNumber(otherDetails.wcSumInsured),
          wcPremiumAmt: moneyToNumber(otherDetails.wcPremiumAmt),
          wcValidFrom: otherDetails.wcValidFrom,
          wcValidTo: otherDetails.wcValidTo,
          wcRemarks: otherDetails.wcRemarks,

          labourLicenceNo: otherDetails.labourLicenceNo,
          securityDeposit: moneyToNumber(otherDetails.securityDeposit),
          labourInform: otherDetails.labourInform,
          labourInstrNo: otherDetails.labourInstrNo,
          labourMode: otherDetails.labourMode,
          labourFrom: otherDetails.labourFrom,
          labourTo: otherDetails.labourTo,
          labourRemarks: otherDetails.labourRemarks,

          esiNo: otherDetails.esiNo,
          esiZone: otherDetails.esiZone,
          esiCircle: otherDetails.esiCircle,
          esiMinNo: otherDetails.esiMinNo,
          esiRemarks: otherDetails.esiRemarks,
        },
      },
    };
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = buildSavePayload();
      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Updated Successfully");
        await loadView();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    restoreSnapshot(initialRef.current);
    toast.info("Restored loaded values");
  };

  const openPopup = (url) => {
    window.open(url, "_blank", "width=820,height=450,resizable=no,scrollbars=yes");
  };

  const totalContract = useMemo(
    () => moneyToNumber(master.contractValueText),
    [master.contractValueText]
  );
  const totalEmd = useMemo(
    () => (emdSecurity || []).reduce((a, r) => a + moneyToNumber(r.amount), 0),
    [emdSecurity]
  );
  const totalPerf = useMemo(
    () =>
      (performanceSecurity || []).reduce((a, r) => a + moneyToNumber(r.amount), 0),
    [performanceSecurity]
  );
  const totalRet = useMemo(
    () => (retention || []).reduce((a, r) => a + moneyToNumber(r.amount), 0),
    [retention]
  );
  const totalSecurity = useMemo(
    () => totalEmd + totalPerf + totalRet,
    [totalEmd, totalPerf, totalRet]
  );

  return (
    <div
      className={`min-h-screen transition-all duration-300 ${
        isDark ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <ToastContainer position="top-right" autoClose={2500} />

      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen((p) => !p)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </h1>
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Contract ID: <b>{contractId || "-"}</b>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsDark((p) => !p)}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                    : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>

              <button
                disabled={loading}
                onClick={handleSave}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                  loading
                    ? "bg-green-600/60 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                } text-white`}
              >
                <FaSave />
                Save
              </button>

              <button
                disabled={loading}
                onClick={handleReset}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                  loading
                    ? "bg-gray-600/60 cursor-not-allowed"
                    : "bg-gray-600 hover:bg-gray-700"
                } text-white`}
              >
                <FaUndo />
                Reset
              </button>

              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
              >
                <FaPrint />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => {
              const active = location.pathname === item.path.split("?")[0];
              return (
                <Link
                  to={item.path}
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                    active
                      ? isDark
                        ? "bg-blue-600 text-white"
                        : "bg-blue-500 text-white"
                      : isDark
                      ? "hover:bg-gray-700 text-gray-300"
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  <item.icon size={18} />
                  {sidebarOpen && (
                    <span className="text-sm font-medium">{item.name}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex-1">
          <div
            className={`transition-all duration-300 ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            <div
              className={`backdrop-blur-md border-b ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1
                      className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaFileInvoiceDollar className="text-blue-500" />
                      RATE/LOA
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      ONGOING PROJECT ENTRY
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() =>
                        openPopup(`/OrderManagement/ProjectBOQdoc?contractid=${contractId}`)
                      }
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <FaEye /> BOQ
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/OrderManagement/DailyProgressMonthWise?contractid=${contractId}`)
                      }
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <FaChartBar /> DPR
                    </button>
                    <button
                      onClick={() =>
                        navigate(
                          `/OrderManagement/ProjectEntryDocumentation?contractId=${contractId}`
                        )
                      }
                      className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <FaFileAlt /> Documentation
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/OrderManagement/PhotoDocument?contractId=${contractId}`)
                      }
                      className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <FaBuilding /> Photos
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <Section isDark={isDark} title="PROJECT / CONTRACT DETAILS" color="bg-blue-600">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <Label isDark={isDark} required>
                      Contract Name
                    </Label>
                    <Input
                      isDark={isDark}
                      value={master.contractName}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, contractName: e.target.value }))
                      }
                      placeholder="Contract Name"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Client Ref Value</Label>
                    <Input
                      isDark={isDark}
                      value={master.clientRefValue}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, clientRefValue: e.target.value }))
                      }
                      placeholder="Client Ref Value"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Client Name</Label>
                    <TextArea
                      isDark={isDark}
                      rows={2}
                      value={master.clientName}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, clientName: e.target.value }))
                      }
                      placeholder="Client Name"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark} required>
                      Work Location
                    </Label>
                    <Select
                      isDark={isDark}
                      value={master.workLocationId || "Select"}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, workLocationId: e.target.value }))
                      }
                    >
                      <option value="Select">Select</option>
                      {workLocations.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Label isDark={isDark}>Nature of Work</Label>
                    <TextArea
                      isDark={isDark}
                      rows={2}
                      value={master.natureOfWork}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, natureOfWork: e.target.value }))
                      }
                      placeholder="Nature of Work"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>SRC ID No</Label>
                    <Input
                      isDark={isDark}
                      value={master.srcIdNo}
                      onChange={(e) => setMaster((p) => ({ ...p, srcIdNo: e.target.value }))}
                      placeholder="SRC ID No"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Branch</Label>
                    <Select
                      isDark={isDark}
                      value={master.branchId || "Select"}
                      onChange={(e) => setMaster((p) => ({ ...p, branchId: e.target.value }))}
                    >
                      <option value="Select">Select</option>
                      {branches.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Label isDark={isDark}>Contract Value</Label>
                    <Input
                      isDark={isDark}
                      value={master.contractValueText}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, contractValueText: e.target.value }))
                      }
                      placeholder="Rs.0.00"
                    />
                    <div className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Parsed: ₹ {fmtMoneyIN(master.contractValueText)}
                    </div>
                  </div>

                  <div>
                    <Label isDark={isDark}>Contract Type</Label>
                    <Input
                      isDark={isDark}
                      value={master.contractType}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, contractType: e.target.value }))
                      }
                      placeholder="Contract Type"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Client Contact</Label>
                    <Input
                      isDark={isDark}
                      value={master.clientContact}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, clientContact: e.target.value }))
                      }
                      placeholder="Client Contact"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>PMC Ref Value</Label>
                    <Input
                      isDark={isDark}
                      value={master.pmcRefValue}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, pmcRefValue: e.target.value }))
                      }
                      placeholder="PMC Ref Value"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>PMC Name</Label>
                    <Input
                      isDark={isDark}
                      value={master.pmcName}
                      onChange={(e) => setMaster((p) => ({ ...p, pmcName: e.target.value }))}
                      placeholder="PMC Name"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>PMC Contact</Label>
                    <Input
                      isDark={isDark}
                      value={master.pmcContact}
                      onChange={(e) => setMaster((p) => ({ ...p, pmcContact: e.target.value }))}
                      placeholder="PMC Contact"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Consultant Ref Value</Label>
                    <Input
                      isDark={isDark}
                      value={master.consultantRefValue}
                      onChange={(e) =>
                        setMaster((p) => ({
                          ...p,
                          consultantRefValue: e.target.value,
                        }))
                      }
                      placeholder="Consultant Ref Value"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Consultant Name</Label>
                    <Input
                      isDark={isDark}
                      value={master.consultantName}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, consultantName: e.target.value }))
                      }
                      placeholder="Consultant Name"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Consultant Contact</Label>
                    <Input
                      isDark={isDark}
                      value={master.consultantContact}
                      onChange={(e) =>
                        setMaster((p) => ({
                          ...p,
                          consultantContact: e.target.value,
                        }))
                      }
                      placeholder="Consultant Contact"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>LOA No.</Label>
                    <Input
                      isDark={isDark}
                      value={master.loaNo}
                      onChange={(e) => setMaster((p) => ({ ...p, loaNo: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Agreement No.</Label>
                    <Input
                      isDark={isDark}
                      value={master.agreementNo}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, agreementNo: e.target.value }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <Label isDark={isDark}>Reason of Delay</Label>
                    <TextArea
                      isDark={isDark}
                      rows={2}
                      value={master.reasonOfDelay}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, reasonOfDelay: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>LD Clause</Label>
                    <Input
                      isDark={isDark}
                      value={master.ldClause}
                      onChange={(e) => setMaster((p) => ({ ...p, ldClause: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Defect Liability</Label>
                    <Input
                      isDark={isDark}
                      value={master.defectLiability}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, defectLiability: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Contract Duration (Days)</Label>
                    <Input
                      isDark={isDark}
                      value={master.contractDuration}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, contractDuration: e.target.value }))
                      }
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Date of LOA</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.dateOfLoa}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, dateOfLoa: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Date of Agreement</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.dateOfAgreement}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, dateOfAgreement: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Start Date</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.startDate}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, startDate: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Completion Date (as per Contract)</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.completionDate}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, completionDate: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Antip Date of Completion</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.antipDate}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, antipDate: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Defect Liability Period From</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.defectPeriodFrom}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, defectPeriodFrom: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Defect Liability Period To</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      value={master.defectPeriodTo}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, defectPeriodTo: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Material Supply by Client</Label>
                    <Select
                      isDark={isDark}
                      value={master.materialSupplyStatus ? "Yes" : "No"}
                      onChange={(e) =>
                        setMaster((p) => ({
                          ...p,
                          materialSupplyStatus: e.target.value === "Yes" ? 1 : 0,
                        }))
                      }
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </Select>
                  </div>

                  <div>
                    <Label isDark={isDark}>Extension Time</Label>
                    <Select
                      isDark={isDark}
                      value={master.extensionTimeStatus ? "Yes" : "No"}
                      onChange={(e) =>
                        setMaster((p) => ({
                          ...p,
                          extensionTimeStatus: e.target.value === "Yes" ? 1 : 0,
                          extensionTime: e.target.value === "Yes" ? p.extensionTime : "",
                        }))
                      }
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </Select>
                  </div>

                  <div>
                    <Label isDark={isDark}>Extension Time Date</Label>
                    <Input
                      isDark={isDark}
                      type="date"
                      disabled={!master.extensionTimeStatus}
                      value={master.extensionTime}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, extensionTime: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label isDark={isDark}>Supplier Ref 1</Label>
                    <Input
                      isDark={isDark}
                      value={master.supRef1}
                      onChange={(e) => setMaster((p) => ({ ...p, supRef1: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label isDark={isDark}>Supplier Value 1</Label>
                    <Input
                      isDark={isDark}
                      value={master.supValue1}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, supValue1: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label isDark={isDark}>Supplier Ref 2</Label>
                    <Input
                      isDark={isDark}
                      value={master.supRef2}
                      onChange={(e) => setMaster((p) => ({ ...p, supRef2: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label isDark={isDark}>Supplier Value 2</Label>
                    <Input
                      isDark={isDark}
                      value={master.supValue2}
                      onChange={(e) =>
                        setMaster((p) => ({ ...p, supValue2: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </Section>

              <TableShell
                isDark={isDark}
                title="EMD / BID SECURITY AMOUNT"
                rows={emdSecurity}
                setRows={setEmdSecurity}
                rowFactory={emptyEmdRow}
                columns={[
                  {
                    key: "dataValue",
                    title: "Data Value",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.dataValue || ""}
                        onChange={(e) => onCell(index, "dataValue", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    title: "Amount",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.amount || ""}
                        onChange={(e) => onCell(index, "amount", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "inForm",
                    title: "In Form",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.inForm || ""}
                        onChange={(e) => onCell(index, "inForm", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "instrumentNo",
                    title: "Instrument No",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.instrumentNo || ""}
                        onChange={(e) => onCell(index, "instrumentNo", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "from",
                    title: "Valid From",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.from || ""}
                        onChange={(e) => onCell(index, "from", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "to",
                    title: "Valid To",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.to || ""}
                        onChange={(e) => onCell(index, "to", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "mode",
                    title: "Mode",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.mode || ""}
                        onChange={(e) => onCell(index, "mode", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "remarks",
                    title: "Remarks",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.remarks || ""}
                        onChange={(e) => onCell(index, "remarks", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "receivedOn",
                    title: "Received On",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.receivedOn || ""}
                        onChange={(e) => onCell(index, "receivedOn", e.target.value)}
                      />
                    ),
                  },
                ]}
              />

              <TableShell
                isDark={isDark}
                title="PERFORMANCE SECURITY"
                rows={performanceSecurity}
                setRows={setPerformanceSecurity}
                rowFactory={emptyPerfRow}
                columns={[
                  {
                    key: "dataValue",
                    title: "Data Value",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.dataValue || ""}
                        onChange={(e) => onCell(index, "dataValue", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    title: "Amount",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.amount || ""}
                        onChange={(e) => onCell(index, "amount", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "inForm",
                    title: "In Form",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.inForm || ""}
                        onChange={(e) => onCell(index, "inForm", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "instrumentNo",
                    title: "Instrument No",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.instrumentNo || ""}
                        onChange={(e) => onCell(index, "instrumentNo", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "from",
                    title: "Valid From",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.from || ""}
                        onChange={(e) => onCell(index, "from", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "to",
                    title: "Valid To",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.to || ""}
                        onChange={(e) => onCell(index, "to", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "mode",
                    title: "Mode",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.mode || ""}
                        onChange={(e) => onCell(index, "mode", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "remarks",
                    title: "Remarks",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.remarks || ""}
                        onChange={(e) => onCell(index, "remarks", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "receivedOn",
                    title: "Received On",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.receivedOn || ""}
                        onChange={(e) => onCell(index, "receivedOn", e.target.value)}
                      />
                    ),
                  },
                ]}
              />

              <TableShell
                isDark={isDark}
                title="MOBILISATION DETAILS"
                rows={mobilisation}
                setRows={setMobilisation}
                rowFactory={emptyMobRow}
                columns={[
                  {
                    key: "dataValue",
                    title: "Data Value",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.dataValue || ""}
                        onChange={(e) => onCell(index, "dataValue", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    title: "Amount",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.amount || ""}
                        onChange={(e) => onCell(index, "amount", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "interest",
                    title: "Interest",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.interest || ""}
                        onChange={(e) => onCell(index, "interest", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "inForm",
                    title: "In Form",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.inForm || ""}
                        onChange={(e) => onCell(index, "inForm", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "instrumentNo",
                    title: "Instrument No",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.instrumentNo || ""}
                        onChange={(e) => onCell(index, "instrumentNo", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "from",
                    title: "From Date",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.from || ""}
                        onChange={(e) => onCell(index, "from", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "to",
                    title: "To Date",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.to || ""}
                        onChange={(e) => onCell(index, "to", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "mode",
                    title: "Mode",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.mode || ""}
                        onChange={(e) => onCell(index, "mode", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "remarks",
                    title: "Remarks",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.remarks || ""}
                        onChange={(e) => onCell(index, "remarks", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "receivedOn",
                    title: "Received On",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.receivedOn || ""}
                        onChange={(e) => onCell(index, "receivedOn", e.target.value)}
                      />
                    ),
                  },
                ]}
              />

              <TableShell
                isDark={isDark}
                title="RETENTION AMOUNT"
                rows={retention}
                setRows={setRetention}
                rowFactory={emptyRetentionRow}
                columns={[
                  {
                    key: "dataValue",
                    title: "Data Value",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.dataValue || ""}
                        onChange={(e) => onCell(index, "dataValue", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    title: "Amount",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.amount || ""}
                        onChange={(e) => onCell(index, "amount", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "inForm",
                    title: "In Form",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.inForm || ""}
                        onChange={(e) => onCell(index, "inForm", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "instrumentNo",
                    title: "Instrument No",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.instrumentNo || ""}
                        onChange={(e) => onCell(index, "instrumentNo", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "from",
                    title: "Valid From",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.from || ""}
                        onChange={(e) => onCell(index, "from", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "to",
                    title: "Valid To",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.to || ""}
                        onChange={(e) => onCell(index, "to", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "mode",
                    title: "Mode",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.mode || ""}
                        onChange={(e) => onCell(index, "mode", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "remarks",
                    title: "Remarks",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        value={row.remarks || ""}
                        onChange={(e) => onCell(index, "remarks", e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "receivedOn",
                    title: "Received On",
                    render: ({ row, index, onCell }) => (
                      <Input
                        isDark={isDark}
                        type="date"
                        value={row.receivedOn || ""}
                        onChange={(e) => onCell(index, "receivedOn", e.target.value)}
                      />
                    ),
                  },
                ]}
              />

              <Section isDark={isDark} title="TAX DETAILS" color="bg-cyan-500">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-cyan-500 text-white">
                      <tr>
                        <Th>Tax Type</Th>
                        <Th>Category</Th>
                        <Th>Tax Include</Th>
                        <Th>Reimbursable</Th>
                        <Th>Percentage</Th>
                        <Th>Remarks</Th>
                      </tr>
                    </thead>
                    <tbody
                      className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
                        isDark ? "divide-gray-700" : "divide-gray-200"
                      }`}
                    >
                      {taxDetails.map((r, i) => (
                        <tr key={i}>
                          <Td>
                            <Input
                              isDark={isDark}
                              value={r.taxType || ""}
                              onChange={(e) =>
                                setTaxDetails((p) => {
                                  const n = [...p];
                                  n[i] = {
                                    ...(n[i] || emptyTaxRow()),
                                    taxType: e.target.value,
                                  };
                                  return n;
                                })
                              }
                            />
                          </Td>
                          <Td>
                            <Input
                              isDark={isDark}
                              value={r.category || ""}
                              onChange={(e) =>
                                setTaxDetails((p) => {
                                  const n = [...p];
                                  n[i] = {
                                    ...(n[i] || emptyTaxRow()),
                                    category: e.target.value,
                                  };
                                  return n;
                                })
                              }
                            />
                          </Td>
                          <Td>
                            <Input
                              isDark={isDark}
                              value={r.include || ""}
                              onChange={(e) =>
                                setTaxDetails((p) => {
                                  const n = [...p];
                                  n[i] = {
                                    ...(n[i] || emptyTaxRow()),
                                    include: e.target.value,
                                  };
                                  return n;
                                })
                              }
                            />
                          </Td>
                          <Td>
                            <Select
                              isDark={isDark}
                              value={Number(r.reimbursable) ? "Yes" : "No"}
                              onChange={(e) =>
                                setTaxDetails((p) => {
                                  const n = [...p];
                                  n[i] = {
                                    ...(n[i] || emptyTaxRow()),
                                    reimbursable: e.target.value === "Yes" ? 1 : 0,
                                  };
                                  return n;
                                })
                              }
                            >
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </Select>
                          </Td>
                          <Td>
                            <Input
                              isDark={isDark}
                              value={r.percentage || ""}
                              onChange={(e) =>
                                setTaxDetails((p) => {
                                  const n = [...p];
                                  n[i] = {
                                    ...(n[i] || emptyTaxRow()),
                                    percentage: e.target.value,
                                  };
                                  return n;
                                })
                              }
                            />
                          </Td>
                          <Td>
                            <Input
                              isDark={isDark}
                              value={r.remarks || ""}
                              onChange={(e) =>
                                setTaxDetails((p) => {
                                  const n = [...p];
                                  n[i] = {
                                    ...(n[i] || emptyTaxRow()),
                                    remarks: e.target.value,
                                  };
                                  return n;
                                })
                              }
                            />
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section
                isDark={isDark}
                title="OTHER DETAILS (POLICIES / LICENSE / ESI)"
                color="bg-cyan-500"
              >
                <div className="space-y-8">
                  <div>
                    <div className={`font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      CAR POLICY
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label isDark={isDark}>Policy No.</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.carPolicyNo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, carPolicyNo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Sum Insured</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.carSumInsured}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, carSumInsured: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Premium Amt</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.carPremiumAmt}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, carPremiumAmt: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Valid From</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.carValidFrom}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, carValidFrom: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Valid To</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.carValidTo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, carValidTo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Remarks</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.carRemarks}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, carRemarks: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={`font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      MAINTENANCE PERIOD
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label isDark={isDark}>From</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.mainPerFrom}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, mainPerFrom: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>To</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.mainPerTo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, mainPerTo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Remarks</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.mainPerRemarks}
                          onChange={(e) =>
                            setOtherDetails((p) => ({
                              ...p,
                              mainPerRemarks: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={`font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      WC POLICY
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label isDark={isDark}>Policy No.</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.wcPolicyNo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, wcPolicyNo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Sum Insured</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.wcSumInsured}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, wcSumInsured: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Premium Amt</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.wcPremiumAmt}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, wcPremiumAmt: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Valid From</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.wcValidFrom}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, wcValidFrom: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Valid To</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.wcValidTo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, wcValidTo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Remarks</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.wcRemarks}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, wcRemarks: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={`font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      LABOUR LICENSE
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label isDark={isDark}>License No.</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.labourLicenceNo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({
                              ...p,
                              labourLicenceNo: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Security Deposit</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.securityDeposit}
                          onChange={(e) =>
                            setOtherDetails((p) => ({
                              ...p,
                              securityDeposit: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>In Form</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.labourInform}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, labourInform: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Instrument No.</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.labourInstrNo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, labourInstrNo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Mode of Release</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.labourMode}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, labourMode: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Remarks</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.labourRemarks}
                          onChange={(e) =>
                            setOtherDetails((p) => ({
                              ...p,
                              labourRemarks: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Valid From</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.labourFrom}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, labourFrom: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Valid To</Label>
                        <Input
                          isDark={isDark}
                          type="date"
                          value={otherDetails.labourTo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, labourTo: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={`font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      ESI
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label isDark={isDark}>ESI No.</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.esiNo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, esiNo: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Zone</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.esiZone}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, esiZone: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Circle</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.esiCircle}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, esiCircle: e.target.value }))
                          }
                        />
                      </div>
                      <div>
                        <Label isDark={isDark}>Minimum No.</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.esiMinNo}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, esiMinNo: e.target.value }))
                          }
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label isDark={isDark}>Remarks</Label>
                        <Input
                          isDark={isDark}
                          value={otherDetails.esiRemarks}
                          onChange={(e) =>
                            setOtherDetails((p) => ({ ...p, esiRemarks: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Section>

              <Section isDark={isDark} title="COMPLETION CERTIFICATE" color="bg-cyan-500">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() =>
                      openPopup(`/OrderManagement/ProjectFinalBillsdoc?contractid=${contractId}`)
                    }
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                  >
                    Final bill copy
                  </button>
                </div>
              </Section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Section isDark={isDark} title="SUMMARY" color="bg-cyan-500">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                      <div className="text-sm text-gray-500">Total Contract Value</div>
                      <div
                        className={`text-xl font-bold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        ₹ {fmtMoneyIN(totalContract)}
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                      <div className="text-sm text-gray-500">
                        Security Amount (EMD+Perf+Ret)
                      </div>
                      <div
                        className={`text-xl font-bold ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        ₹ {fmtMoneyIN(totalSecurity)}
                      </div>
                    </div>
                  </div>
                </Section>

                <Section isDark={isDark} title="ACTIONS" color="bg-blue-600">
                  <div className="flex flex-wrap gap-3">
                    <button
                      disabled={loading}
                      onClick={handleSave}
                      className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading
                          ? "bg-green-600/60 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700"
                      } text-white`}
                    >
                      <FaSave />
                      Save & Continue
                    </button>

                    <button
                      onClick={() => toast.info("Preview: use Print button")}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaEye />
                      Preview
                    </button>

                    <button
                      disabled={loading}
                      onClick={handleReset}
                      className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading
                          ? "bg-gray-600/60 cursor-not-allowed"
                          : "bg-gray-600 hover:bg-gray-700"
                      } text-white`}
                    >
                      <FaUndo />
                      Reset Form
                    </button>
                  </div>

                  {loading ? (
                    <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Working...
                    </div>
                  ) : null}
                </Section>
              </div>

              <div className="h-6" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}